import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import ProfileImage from "../Componets/ProfileImage";
import Constant from "../views/Constant";
import { getRecommendedPosts } from "../views/utilities/ApiCalls";

export default function RecommendedPosts({ id, sourceType = "story" }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) return;

    let mounted = true;

    const loadRecommendations = async () => {
      try {
        setLoading(true);
        const response = await getRecommendedPosts(id, sourceType);
        if (!mounted) return;
        setPosts(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (!mounted) return;
        setPosts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadRecommendations();

    return () => {
      mounted = false;
    };
  }, [id, sourceType]);

  const proxiedUrl = (url) => {
    if (
      !url ||
      url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com") ||
      url.includes("data:image") ||
      url.includes("blob:") ||
      url.startsWith("static/") ||
      url.startsWith("/static")
    ) {
      return url;
    }
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return "";
    const plainText = text.replace(/<[^>]+>/g, "");
    if (plainText.length <= maxLength) return plainText;
    return `${plainText.substring(0, maxLength)}...`;
  };

  const cards = useMemo(() => posts.slice(0, 4), [posts]);

  if (!loading && cards.length === 0) return null;

  return (
    <Wrap>
      <h3 className="heading">Recommended Posts</h3>
      <div className="grid-container">
        {loading
          ? [1, 2].map((item) => (
              <div className="cart skeleton" key={item}>
                <div className="line line-lg"></div>
                <div className="line line-sm"></div>
                <div className="line"></div>
              </div>
            ))
          : cards.map((data) => {
              const isSindhi = (data.language || "").toLowerCase() === "sindhi";
              const route =
                data.contentType === "poem"
                  ? `/poem-details/${data._id}`
                  : `/story-details/${data._id}`;

              return (
                <Link
                  key={`${data.contentType || "story"}_${data._id}`}
                  to={route}
                  style={{ textDecoration: "none", display: "block", height: "100%" }}
                >
                  <div className="cart">
                    <div className="author-info">
                      <ProfileImage
                        src={data.profilePic || data.user?.profilePic}
                        name={data.authorName || data.user?.name || "Author"}
                        size={32}
                        className="author-avatar"
                        alt={data.authorName || data.user?.name || "Author"}
                        proxiedUrl={proxiedUrl}
                        imgStyle={{
                          border: "1px solid var(--color-border)",
                          background: "var(--color-background)",
                        }}
                      />
                      <span className="author-name">
                        {data.authorName || data.user?.name || "Unknown"}
                      </span>
                    </div>

                    <div
                      className="title"
                      style={
                        isSindhi
                          ? { fontFamily: "SHREE-DEV7-0714", fontSize: "16px" }
                          : undefined
                      }
                    >
                      {data.name}
                    </div>

                    {(data.description || data.story) && (
                      <div
                        className={`story-snippet${isSindhi ? " sindhi-text" : ""}`}
                        dangerouslySetInnerHTML={{
                          __html: truncateText(data.description || data.story, 150),
                        }}
                      />
                    )}
                  </div>
                </Link>
              );
            })}
      </div>
    </Wrap>
  );
}

const Wrap = styled.div`
  width: 100%;
  margin-top: 28px;
  margin-bottom: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border);

  .heading {
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 20px;
    color: var(--color-text);
    letter-spacing: -0.3px;
  }

  .grid-container {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;

    @media (max-width: 540px) {
      grid-template-columns: 1fr;
    }
  }

  .cart {
    width: 100%;
    background: var(--color-surface);
    border: 1px solid #eaeaea;
    border-radius: 20px;
    padding: 28px 24px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    display: flex;
    flex-direction: column;
    height: 100%;
    cursor: pointer;
    text-align: left;

    &:hover {
      border-color: var(--color-primary);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.07);
    }
  }

  .author-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .author-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-tertiary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .title {
    font-size: 17px;
    font-weight: 800;
    color: var(--color-text);
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-align: left;
    letter-spacing: 0.1px;
    text-transform: none;
    margin-top: 14px;
    margin-bottom: 8px;
  }

  .story-snippet {
    font-size: 13px;
    line-height: 1.65;
    color: var(--color-text-tertiary);
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-align: left;

    p {
      margin: 0;
      display: inline;
    }
  }

  .story-snippet.sindhi-text,
  .story-snippet.sindhi-text p,
  .story-snippet.sindhi-text strong,
  .story-snippet.sindhi-text em,
  .story-snippet.sindhi-text u {
    font-family: "SHREE-DEV7-0714", "Merriweather", serif;
    font-size: 15px;
  }

  .skeleton {
    cursor: default;
  }

  .line {
    height: 10px;
    border-radius: 6px;
    background: var(--color-border);
    margin-bottom: 10px;
  }

  .line-lg {
    height: 18px;
    width: 70%;
  }

  .line-sm {
    width: 40%;
  }

  @media (max-width: 768px) {
    margin-top: 20px;
    margin-bottom: 0;
    padding-top: 16px;

    .heading {
      font-size: 17px;
      margin-bottom: 14px;
    }

    .grid-container {
      gap: 12px;
    }

    .cart {
      border-radius: 16px;
      padding: 16px 18px;
    }

    .title {
      font-size: 15px;
      margin-top: 12px;
      margin-bottom: 6px;
    }

    .story-snippet {
      font-size: 12px;
      line-height: 1.55;
    }
  }
`;
